import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/apiService';
import { AuthService } from '../services/authService';
import './EmailVerification.css';

interface EmailVerificationProps {
  token: string;
  onVerified: () => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ token, onVerified }) => {
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string>('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const result = await ApiService.verifyEmail(token);
      if (result.success) {
        setStatus('success');
        // Refresh auth state to get updated email_verified status
        await AuthService.checkAuthState();
        // Auto-redirect after 3 seconds
        setTimeout(() => {
          onVerified();
        }, 3000);
      } else {
        setStatus('error');
        setError(result.error || 'Verification failed');
      }
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'An error occurred during verification');
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const result = await ApiService.resendVerificationEmail();
      if (result.success) {
        alert('Verification email sent! Please check your inbox.');
      } else {
        alert(result.error || 'Failed to resend verification email');
      }
    } catch (err: any) {
      alert('Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="email-verification-container">
      <div className="email-verification-card">
        {status === 'verifying' && (
          <>
            <div className="verification-spinner">⏳</div>
            <h2>Verifying Your Email</h2>
            <p>Please wait while we verify your email address...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="verification-success">✅</div>
            <h2>Email Verified Successfully!</h2>
            <p>Your email has been verified. You now have access to all features including leaderboard rankings and full statistics.</p>
            <p className="redirect-message">Redirecting you to the game...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="verification-error">❌</div>
            <h2>Verification Failed</h2>
            <p className="error-message">{error}</p>
            <p>The verification link may have expired or already been used.</p>
            <button 
              onClick={handleResend} 
              disabled={resending}
              className="resend-button"
            >
              {resending ? 'Sending...' : 'Resend Verification Email'}
            </button>
            <button 
              onClick={onVerified}
              className="continue-button"
            >
              Continue to Game
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;

